import { ReactNode, useEffect, useState } from 'react';
import { MaintenanceStatus } from 'src/types/maintenanceStatus';
import { createAxiosInstance } from 'src/utils/axiosInstance';
import { AxiosError, AxiosResponse } from 'axios';
import LoadingSpinner from 'src/components/LoadingSpinner';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';

import './maintenance-middleware.styles.scss';

interface MaintenanceMiddlewareProps {
    children: ReactNode;
}

export const MaintenanceMiddleware = ({
    children,
}: MaintenanceMiddlewareProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    // Stop initial render of page while this loads; otherwise there's an ugly flash && the APIs all fire off
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isInMaintenance, setIsInMaintenance] = useState(false);

    const req = {
        url: '/maintenance/status',
        method: 'GET',
    };

    const axios = createAxiosInstance();
    const checkMaintenanceStatus = async () => {
        try {
            const response = (await axios(req)) as AxiosResponse;

            const data: MaintenanceStatus = response.data as MaintenanceStatus;
            if (data && isInMaintenance) {
                // Send home until there's a better way to track the path in the app
                navigate('/');
                setIsInMaintenance(false);
            }
        } catch (generalError) {
            const error = generalError as unknown as AxiosError;
            if (error.response?.status === 503) {
                // Service Unavailable - treat as maintenance mode
                const data = error.response.data as MaintenanceStatus;
                setIsInMaintenance(true);
                navigate('/maintenance', { replace: true, state: data });
            }
        } finally {
            if (isInitialLoad) {
                setIsInitialLoad(false);
            }
        }
    };

    useEffect(() => {
        checkMaintenanceStatus();
    }, []);

    useEffect(() => {
        // Poll every 30 seconds for maintenance status to see if we've ended early
        // only when there is maintenance happening
        if (location.pathname === '/maintenance') {
            const interval = setInterval(checkMaintenanceStatus, 30000);

            return () => clearInterval(interval);
        }
    });

    return isInitialLoad ? (
        <div className={'loading-container'}>
            <LoadingSpinner />
        </div>
    ) : (
        <>{children}</>
    );
};
